import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';

//CONFIG
import { WarehouseGroupService } from '@app/core/locations/services/warehouse-group.service';
import { FulfillmentOrderApiService } from 'v2/modules/shared/services/api/fulfillment-order-api.service';

import { EExportType, OrderShipFormV2Component } from '../../../order';
import { OrderShipFormComponent } from '../../../order/components/order-ship-form/order-ship-form.component';
import { OrderShippingUpdatesComponent } from '../../../order/components/order-shipping-updates/order-shipping-updates.component';
import {
  ISalesOrder,
  IOrderPickingPreviewDialogInterface,
} from '../../../order/interfaces/order.interface';
import { FeatureFlagUtilService, PackingFacadeService } from '../../../shared';
import { DialogConfig, SnackBarConfig } from '../../../shared/configs';

//INTERFACE
import { IPagination, LocalStorageKeys } from '../../../shared/interfaces';

//COMPONENT

//SERVICE
import { SalesOrderApiService } from '../../../shared/services/api/sales-order-api.service';
import { SystemSettingApiService } from '../../../shared/services/api/system-setting-api.service';

// UTILS
import { trackById, trackByLabel } from '../../../shared/util/track-by.util';
import { BatchCreateInvoiceComponent } from '../../components/sales-order-invoicing/batch-create-invoice/batch-create-invoice.component';
import { ISalesOrderWithInvoicing } from '../../components/sales-order-invoicing/batch-create-invoice/batch-invoice.interface';
import { SalesOrderAgTableConfig } from '../../configs';
import {
  CSalesOrderStatusConstant,
  EQuickFulfillmentActions,
  ESalesOrderSelectAllAction,
  ESalesOrderStatusValue,
  TSalesOrderStatus,
  TUpdateStatusPayload,
} from '../../interfaces/sales-order.interface';
import { PreviewPicklistUtilsService } from '../../services/preview-picklist-utils.service';
import {
  ISalesOrderConfirmationDialogData,
  SalesOrderConfirmationDialogComponent,
} from '../sales-order-confirmation-dialog/sales-order-confirmation-dialog.component';
import { SalesOrderFacadeService } from '../sales-order-facade.service';
import { SalesOrderFormComponent } from '../sales-order-form/sales-order-form.component';
import { SalesOrderPicklistConfirmActionComponent } from '../sales-order-picklist-confirm-action/sales-order-picklist-confirm-action.component';
import { SalesOrderPullOrderFormComponent } from '../sales-order-pull-order-form/sales-order-pull-order-form.component';
import { SalesOrderPullOrdersFormComponent } from '../sales-order-pull-orders-form/sales-order-pull-orders-form.component';
import {
  ISalesOrderUpdateStatusFormResult,
  SalesOrderUpdateStatusFormComponent,
} from '../sales-order-update-status-form/sales-order-update-status-form.component';

import { FeatureToggleService } from './../../../../../app/shared/services/feature-toggle.service';
import { OrderPostedTagsFormComponent } from './../../../order/components/order-posted-tags-form/order-posted-tags-form.component';
import { PicklistPreviewDialogComponent } from './../../../picklist/components/picklist-preview-dialog/picklist-preview-dialog.component';
import { OrderSalesApiService } from './../../../shared/services/api/order/order-sales-api.service';
import { PicklistApiService } from './../../../shared/services/api/picklist-api.service';
import { LoadingDialogUtilService } from './../../../shared/services/util/loading-dialog-util.service';
import { UiConfirmationDialogComponent } from './../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';

@Component({
  selector: 'lum-sales-order-bulk-action',
  templateUrl: './sales-order-bulk-action.component.html',
  styleUrls: ['./sales-order-bulk-action.component.scss'],
})
export class SalesOrderBulkActionComponent implements OnInit, OnDestroy {
  @Input()
  selectedRows :any[] = [];

  @Input()
  currentQueryParams :any = {};

  @Input()
  isSelectAllRows :boolean = false;

  @Input()
  pagination :IPagination = {
    current_page: 1,
    from: null,
    last_page: 1,
    per_page: 100,
    to: null,
    total: 0,
    pageIndex: null,
  };

  @Input()
  ftPickAndPackEnabled :boolean;

  @Input()
  ftEdiIntegrationEnabled :boolean;

  @Input()
  canMarkAsShipped :boolean;

  @Input()
  warehouseGroupId :number = 0;

  @Input()
  inventoryPickAndPackEnabled :boolean = false;

  @Input()
  picklistPreviewAction :string = '';

  @Input()
  allSelectableRowCount :number;

  @Input()
  selecatableRowData :ISalesOrder[] = [];

  @Input()
  remainingSelectableCount :number = 0;

  @Input()
  channelsHasPushOrderMapping :boolean;

  @Output()
  refreshTable :EventEmitter<void> = new EventEmitter();

  @Output()
  resetTable :EventEmitter<void> = new EventEmitter();

  @Output()
  updateSelectedRows :EventEmitter<any[]> = new EventEmitter();

  @Output()
  previewPicklistItemsEvent :EventEmitter<ISalesOrder[]> = new EventEmitter();

  @Output()
  onSyncJobs :EventEmitter<boolean> = new EventEmitter();

  @Output()
  onGroupByChangeEvent :EventEmitter<string> = new EventEmitter();

  @Output()
  onOmsFilterChangeEvent :EventEmitter<number | null> = new EventEmitter();

  @Output()
  onClickCreateCustomFieldEvent :EventEmitter<boolean> = new EventEmitter();

  previewPicklistAction$ :Observable<string>;
  toolTipMessage :string = '';
  settings :any = {};
  currentGroupBy :string = '';
  groupByOptions = SalesOrderAgTableConfig.groupByColumns;
  currentOmsFilterId :number | null = null;
  eExportType = EExportType;
  ftFulfillmentOrderShipmentSplits :boolean = false;

  trackByLabel = trackByLabel;
  trackById = trackById;

  private onDestroy$ = new Subject();

  constructor(
    private router :Router,
    private dialog :MatDialog,
    private orderSalesApiService :OrderSalesApiService,
    private picklistApiService :PicklistApiService,
    private previewPicklistUtilsService :PreviewPicklistUtilsService,
    private systemSettingApiService :SystemSettingApiService,
    private snackBar :MatSnackBar,
    private salesOrderApiService :SalesOrderApiService,
    private loadingDialogService :LoadingDialogUtilService,
    private packingFacadeService :PackingFacadeService,
    private cd :ChangeDetectorRef,
    private featureFlagUtilService :FeatureFlagUtilService,
    private ft :FeatureToggleService,
    private salesOrderFacadeService :SalesOrderFacadeService,
    private warehouseGroupService :WarehouseGroupService,
    private fulfillmentOrderApiService :FulfillmentOrderApiService,
  ) {}

  ngOnInit() :void {
    this.initGroupState();
    this.getSettings();
    this.getDataFromService();
    this.getPreviewPicklistAction();
    this.ftFulfillmentOrderShipmentSplits = this.ft.isActive(
      'red_DV-3362_fo_shipment_splits',
    );
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onBulkDelete() :void {
    const salesOrders :{ id :number }[] = this.selectedRows
      .filter((selectedRow :ISalesOrder | null) => !!selectedRow)
      .map((selectedRow :ISalesOrder) => ({ id: selectedRow.id }));

    const updateStatusPayload :{
      salesOrders :{ id :number }[];
      rollbackInventory ?:boolean;
    } = {
      salesOrders,
    };

    const data :ISalesOrderConfirmationDialogData = {
      salesOrderIds: salesOrders.map(({ id }) => id),
    };

    this.dialog
      .open(SalesOrderConfirmationDialogComponent, {
        ...DialogConfig.MEDIUM,
        data,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (
          result :| {
                dismiss :boolean;
                errors ?:string[];
                value ?:boolean;
              }
            | boolean,
        ) => {
          if (typeof result === 'object' && !result.dismiss) {
            if (result.value === true) {
              updateStatusPayload.rollbackInventory = true;
            }

            this.loadingDialogService.show('Deleting Sales Orders, please wait...');

            this.orderSalesApiService
              .bulkDeleteOrders(updateStatusPayload)
              .pipe(takeUntil(this.onDestroy$))
              .subscribe({
                next: (response) => {
                  this.loadingDialogService.close();

                  this.snackBar.open(response.message, 'Success', {
                    duration: SnackBarConfig.DEFAULT.duration,
                  });
                  this.refreshTable.emit();
                },
                error: (response) => {
                  this.loadingDialogService.close();

                  this.snackBar.open(response.error, 'Fail', {
                    duration: SnackBarConfig.DEFAULT.duration,
                  });
                  this.refreshTable.emit();
                },
              });
          }
        },
      );
  }

  onBulkUpdateTags() :void {
    this.dialog
      .open(OrderPostedTagsFormComponent, {
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((tags) => {
        if (tags) {
          const selectedIds :number[] = this.selectedRows
            .filter((selectedRow :ISalesOrder | null) => !!selectedRow)
            .map((selectedRow :ISalesOrder) => selectedRow.id);

          this.loadingDialogService.show(
            'Updating Sales Orders Tags, please wait...',
          );

          if (this.isSelectAllRows) {
            const params :{ [param :string] :string | any } = {
              salesOrderParams: this.currentQueryParams,
              action: ESalesOrderSelectAllAction.UPDATE_TAGS,
              data: { tags: tags },
            };
            this.selectAllAction(params);

            return;
          }

          this.orderSalesApiService
            .bulkUpdateTags({
              selectedIds: selectedIds,
              tags: tags,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.loadingDialogService.close();

                this.snackBar.open(response.success, 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
                this.refreshTable.emit();
              },
              error: (response) => {
                this.loadingDialogService.close();

                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
                this.refreshTable.emit();
              },
            });
        }
      });
  }

  onGetOrderUpdates() :void {
    const salesOrderData = this.isSelectAllRows
      ? this.selecatableRowData
      : this.selectedRows;

    this.dialog
      .open(OrderShippingUpdatesComponent, {
        data: {
          title: 'Get Order Shipping Updates',
          featureFlags: {},
          salesOrders: salesOrderData,
          isSelectAllRows: this.isSelectAllRows,
          remainingSelectableCount: this.remainingSelectableCount,
        },
        width: DialogConfig.sizes.large,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((response) => {
        if (response.isSelectAllRows) {
          this.loadingDialogService.show(
            'Running Get Order Updates Job, please wait...',
          );

          const params :{ [param :string] :string | any } = {
            salesOrderParams: this.currentQueryParams,
            action: ESalesOrderSelectAllAction.GET_ORDER_UPDATES,
          };
          this.selectAllAction(params);

          return;
        }
        if (response.hasUpdates) {
          this.refreshTable.emit();
        }
      });
  }

  onBulkUpdateOrderStatus() :void {
    if (this.selectedRows?.length === 0 && !this.isSelectAllRows) {
      this.snackBar.open('No record selected.', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    if (
      this.hasMultipleWarehouseGroupIDs() ||
      this.isInvalidWarehouseGroupFromSelectAllAction
    ) {
      this.snackBar.open(
        'Selected orders must have the same warehouse group ID before updating order status. You cannot select orders with different warehouse group IDs.',
        'Fail',
        { duration: SnackBarConfig.DEFAULT.duration },
      );

      return;
    }

    const inputOptions :Record<string, any> = {};
    const skippedStatus :string[] = [
      'AWAITING_SHIPMENT',
      'PARTIALLY_SHIPPED',
      'ON_HOLD',
    ];

    if (this.ftPickAndPackEnabled) {
      const selectedOrders = this.isSelectAllRows
        ? this.selecatableRowData[0]
        : this.selectedRows[0];
      const pickAndPackEnabled = selectedOrders?.warehouseGroup?.pickAndPackEnabled;

      if (!selectedOrders.warehouseGroup || pickAndPackEnabled) {
        skippedStatus.push(ESalesOrderStatusValue.SHIPPED.toUpperCase());
      }
    }

    for (const [statusKey, statusValue] of Object.entries(this.orderStatus)) {
      if (skippedStatus.includes(statusKey)) {
        continue;
      }

      inputOptions[statusValue] = statusKey;
    }

    this.dialog
      .open(SalesOrderUpdateStatusFormComponent, {
        data: {
          title: 'Update Order Status',
          orderStatus: inputOptions,
          hideAdditionalFields:
            this.isSelectAllRows || this.selectedRows.length > 1,
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result :ISalesOrderUpdateStatusFormResult) => {
        const { orderStatus, trackingNumber, carrierCode } = result;

        if (orderStatus) {
          this.loadingDialogService.show('Updating order status, please wait...');
          this.processBulkUpdateOrderStatus(this.selectedRows, result);
        }
      });
  }

  onBulkCreatePicklist() :void {
    if (!this.canCreatePicklist) {
      this.snackBar.open(this.toolTipMessage, 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }
    this.redirectAndCreatePicklist();
  }

  onBulkCreateIndividualPicklistsPerOrder() :void {
    if (this.selectedRows?.length === 0 && !this.isSelectAllRows) {
      this.snackBar.open('No record selected.', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    if (this.isSelectAllRows) {
      const params :{ [param :string] :string | any } = {
        salesOrderParams: this.currentQueryParams,
        action: ESalesOrderSelectAllAction.CREATE_PICKLIST_PER_ORDER,
      };
      this.selectAllAction(params);

      return;
    }

    this.createPicklistsPerOrder(false);
  }

  onBulkExportPackingSlips() {
    const selectedIds :number[] = this.selectedRows
      .filter((selectedRow :ISalesOrder | null) => !!selectedRow)
      .map((selectedRow :ISalesOrder) => selectedRow.id);

    this.packingFacadeService.exportPackingSlipForOrdersToPdf(selectedIds);
  }

  onBulkExportSalesOrders() {
    const selectedIds :number[] = this.selectedRows
      .filter((selectedRow :ISalesOrder | null) => !!selectedRow)
      .map((selectedRow :ISalesOrder) => selectedRow.id);

    this.salesOrderFacadeService.exportBulkSalesOrder(
      selectedIds,
      this.eExportType.XLSX,
    );
  }

  onBulkExportFulfillmentAllocationSheet() {
    const selectedIds :number[] = this.selectedRows
      .filter((selectedRow :ISalesOrder | null) => !!selectedRow)
      .map((selectedRow :ISalesOrder) => selectedRow.id);

    this.salesOrderFacadeService.exportFulfillmentAllocationSheet(selectedIds);
  }

  onAutoGenerateFulfillmentOrders() :void {
    const salesOrderData = this.isSelectAllRows
      ? this.selecatableRowData
      : this.selectedRows;

    const salesOrderIds :number[] = [];
    const notAutoFulfillableOrders :string[] = [];

    salesOrderData.forEach((order) => {
      salesOrderIds.push(order.id);

      if (order.salesOrderShipments && order.salesOrderShipments.length > 0) {
        notAutoFulfillableOrders.push(order.orderNumber);
      }
    });

    if (notAutoFulfillableOrders.length > 0) {
      this.snackBar.open(
        'Can not proceed. Sales orders have fulfillment orders already: ' +
          notAutoFulfillableOrders.join(', '),
        'Fail',
      );

      return;
    }

    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Auto Generate Fulfillment Orders',
          message: 'This action will auto generate fulfillment orders. Proceed?',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loadingDialogService.show('Auto generating fulfillment order...');
          this.fulfillmentOrderApiService
            .autoGenerateFulfillmentOrder({ salesOrderIds: salesOrderIds })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response :any) => {
                this.loadingDialogService.close();
                this.snackBar.open(
                  response.success ||
                    response[0]?.message ||
                    'Auto generate fulfillment order completed.',
                  response[0]?.status || 'Success',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );
                this.refreshTable.emit();
              },
              error: (response :any) => {
                this.loadingDialogService.close();
                this.snackBar.open(
                  response.error ||
                    response?.error?.message ||
                    response[0]?.message ||
                    'Failed to auto generate fulfillment order(s).',
                  response[0]?.status || 'Fail',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );
              },
            });
        }
      });
  }

  onOpenShipOrders() :void {
    const salesOrderData = this.isSelectAllRows
      ? this.selecatableRowData
      : this.selectedRows;

    if (this.ftPartialFulfillment) {
      this.dialog
        .open(OrderShipFormV2Component, {
          data: {
            title: 'Fulfill Orders',
            salesOrders: salesOrderData,
            isSelectAllRows: this.isSelectAllRows,
            ftEdiIntegrationEnabled: this.ftEdiIntegrationEnabled,
          },
          width: DialogConfig.sizes['xx-large'],
        })
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((response :any) => {
          if (response) {
            this.loadingDialogService.show(
              'Running Fulfill Orders Job, please wait...',
            );
            const params :{ [param :string] :string | any } = {
              salesOrderParams: this.currentQueryParams,
              action: ESalesOrderSelectAllAction.SHIP_ORDERS,
              data: {
                excludedOrderItemIds: response,
              },
            };
            this.selectAllAction(params);

            return;
          } else {
            this.refreshTable.emit();
          }
        });

      return;
    }

    this.dialog
      .open(OrderShipFormComponent, {
        data: {
          title: 'Fulfill Orders',
          salesOrders: salesOrderData,
          isSelectAllRows: this.isSelectAllRows,
          remainingSelectableCount: this.remainingSelectableCount,
          ftEdiIntegrationEnabled: this.ftEdiIntegrationEnabled,
        },
        width: DialogConfig.sizes.large,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((response :boolean) => {
        if (response) {
          this.loadingDialogService.show(
            'Running Fulfill Orders Job, please wait...',
          );
          const params :{ [param :string] :string | any } = {
            salesOrderParams: this.currentQueryParams,
            action: ESalesOrderSelectAllAction.SHIP_ORDERS,
          };
          this.selectAllAction(params);

          return;
        } else {
          this.refreshTable.emit();
        }
      });
  }

  onPreviewPicklistItems() :void {
    if (!this.hasNoPostedOrder) {
      this.snackBar.open(this.toolTipMessage, 'OK', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
    }
    const salesOrders = this.selectedRows.filter(
      (order :ISalesOrder) => order.isPosted,
    );
    this.previewPicklistItemsEvent.emit(salesOrders);
  }

  onGroupByChange(value :string = '') :void {
    this.currentGroupBy = value;
    this.onGroupByChangeEvent.emit(value);
  }

  onClickCreateCustomField() :void {
    this.onClickCreateCustomFieldEvent.emit(true);
  }

  onCreateSalesOrder() :void {
    this.dialog
      .open(SalesOrderFormComponent, {
        ...DialogConfig.XX_LARGE,
        panelClass: 'sales-order-form-dialog',
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((response) => {
        if (response) {
          this.refreshTable.emit();
        }
      });
  }

  onPullOrders() :void {
    this.dialog.open(SalesOrderPullOrdersFormComponent, {
      width: DialogConfig.sizes.small,
    });
  }

  onPullOrderShipments() :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Pull Order Shipments',
          message: 'Are you sure you want to pull order shipments?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.orderSalesApiService
            .pullShipments()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (resp) => {
                this.snackBar.open(resp?.success, 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
              },
              error: (error) => {
                this.snackBar.open(error?.error, 'Error', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
              },
            });
        }
      });
  }

  onPullSingleOrder() :void {
    this.dialog.open(SalesOrderPullOrderFormComponent, {
      width: DialogConfig.sizes.large,
    });
  }

  onUnpostOrders() :void {
    const salesOrderIds = [];
    this.selectedRows
      .filter((selectedRow :ISalesOrder | null) => !!selectedRow)
      .forEach((selectedRow :ISalesOrder) => {
        salesOrderIds.push(selectedRow.id);
      });

    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Unallocate Order',
          message:
            'This action will unallocate the selected orders and will be reposted in Purgatory Page. Proceed?',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loadingDialogService.show('Unposting orders...');
          this.orderSalesApiService
            .unpostOrders({
              salesOrderIds: salesOrderIds.join(','),
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.loadingDialogService.close();
                this.snackBar.open(response.success, 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                this.refreshTable.emit();
              },
              error: (response) => {
                this.loadingDialogService.close();
                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                this.refreshTable.emit();
              },
            });
        }
      });
  }

  onClearGridState() :void {
    this.resetTable.emit();
  }

  private getDataFromService() :void {
    this.previewPicklistAction$ = this.previewPicklistUtilsService.action$;
  }

  private redirectAndCreatePicklist() {
    if (this.redirectToPickAndPack) {
      this.createPicklist(false);

      return;
    }

    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Create picklist',
          message:
            'Do you want to create a picklist for these selected sales orders?',
          input: {
            type: 'checkbox',
            label: 'Automatically move inventory',
          },
        },
        width: DialogConfig.sizes.medium,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result :{ dismiss :boolean; value ?:boolean } | boolean) => {
        if (typeof result === 'object' && !result.dismiss) {
          this.createPicklist(result.value || false);
        }
      });
  }

  private createPicklist(updateInventory :boolean) :void {
    const orderIds :number[] = this.selectedRows
      .filter((selectedRow :ISalesOrder | null) => !!selectedRow)
      .map((selectedRow :ISalesOrder) => selectedRow.id);

    this.loadingDialogService.show(
      'Creating Picklist for Sales Orders, please wait...',
    );

    this.picklistApiService
      .bulkCreatePicklist({
        salesOrders: orderIds,
        fulfill: updateInventory,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loadingDialogService.close();

          if (response && response.error) {
            this.handlePicklistErrorResponse(response, updateInventory);

            return;
          }

          this.snackBar.open(
            response.success || 'Picklist successfully created',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );

          if (this.redirectToPickAndPack) {
            if (response && response.id) {
              this.router.navigateByUrl(`/shipping/picklists/${response.id}`);
            } else {
              this.router.navigateByUrl('/shipping/picklists');
            }
          }

          this.refreshTable.emit();
        },
        error: (response) => {
          this.loadingDialogService.close();

          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          this.refreshTable.emit();
        },
      });
  }

  private createPicklistsPerOrder(updateInventory :boolean) :void {
    const orderIds :number[] = this.selectedRows
      .filter((selectedRow :ISalesOrder | null) => !!selectedRow)
      .map((selectedRow :ISalesOrder) => selectedRow.id);

    this.loadingDialogService.show(
      'Creating individual picklists per Sales Order, please wait...',
    );

    this.picklistApiService
      .bulkCreatePicklist({
        salesOrders: orderIds,
        fulfill: updateInventory,
        createPerOrder: true,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loadingDialogService.close();

          if (
            response &&
            response.error &&
            Array.isArray(response.error) &&
            response.error.length > 0
          ) {
            this.handlePicklistPerOrderErrorResponse(response);

            return;
          }

          this.snackBar.open(
            response.success || 'Picklist successfully created',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );

          this.refreshTable.emit();
        },
        error: (response) => {
          this.loadingDialogService.close();

          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          this.refreshTable.emit();
        },
      });
  }

  private processBulkUpdateOrderStatus(
    salesOrders :ISalesOrder[],
    data :ISalesOrderUpdateStatusFormResult,
  ) :void {
    const { orderStatus, trackingNumber, carrierCode } = data;

    const updateStatusPayload :TUpdateStatusPayload = {
      salesOrders: salesOrders,
      orderStatus: orderStatus,
      trackingNumber: trackingNumber,
      carrierCode: carrierCode,
    };
    if (this.isSelectAllRows) {
      const params :{ [param :string] :string | any } = {
        salesOrderParams: this.currentQueryParams,
        action: ESalesOrderSelectAllAction.UPDATE_ORDER_STATUS,
        data: {
          orderStatus: orderStatus,
          trackingNumber: trackingNumber,
          carrierCode: carrierCode,
        },
      };
      this.selectAllAction(params);

      return;
    }
    this.salesOrderApiService
      .bulkUpdateOrderStatus(updateStatusPayload)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loadingDialogService.close();
          if (response && response.error) {
            this.snackBar.open(response.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });

            return;
          }

          if (
            response?.data?.length &&
            this.ft.featureEnabled('red_CS-3146_pickandpack_over_manual_updates')
          ) {
            this.dialog.open(SalesOrderPicklistConfirmActionComponent, {
              data: {
                orders: response.data,
              },
              width: DialogConfig.sizes.large,
            });
          } else {
            this.snackBar.open(response.success, 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }

          this.refreshTable.emit();
        },
        error: (response) => {
          this.loadingDialogService.close();
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          this.refreshTable.emit();
        },
      });
  }

  private selectAllAction(params :{ [param :string] :string | string[] }) :void {
    this.orderSalesApiService
      .selectAllAction(params)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loadingDialogService.close();
          if (response && response.error) {
            this.snackBar.open(response.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });

            return;
          }

          this.snackBar.open(response.success, 'Success', {
            duration: 5000, //5 seconds
          });

          this.refreshTable.emit();
          this.onSyncJobs.emit(true);
        },
        error: (response) => {
          this.loadingDialogService.close();
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private handlePicklistErrorResponse(
    response :{
      error :string;
      data :IOrderPickingPreviewDialogInterface[];
    },
    updateInventory :boolean = false,
  ) :void {
    this.snackBar.open(response.error, 'Fail', {
      duration: SnackBarConfig.DEFAULT.duration,
    });

    if (!response.data?.length) {
      return;
    }

    this.dialog
      .open(PicklistPreviewDialogComponent, {
        data: {
          pickingPreviewData: response.data,
          errors: [response.error],
        },
        width: DialogConfig.sizes['xx-large'],
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result.length > 0) {
          const selectedRowIds = this.selectedRows.map((item) => item.id);

          const isResultSelectedRowsEqual =
            result.length === selectedRowIds.length &&
            result.every((id :number) => selectedRowIds.includes(id));

          if (!isResultSelectedRowsEqual) {
            this.selectedRows = this.selectedRows.filter(
              (item) => !result.includes(item.id),
            );
            this.updateSelectedRows.emit(this.selectedRows);
          }
          this.createPicklist(updateInventory);
        }
      });
  }

  private handlePicklistPerOrderErrorResponse(response :{
    error :string[];
    data :IOrderPickingPreviewDialogInterface[];
  }) :void {
    this.dialog.open(PicklistPreviewDialogComponent, {
      data: {
        pickingPreviewData: response.data,
        errors: response.error,
        hideActionButtons: true,
      },
      width: DialogConfig.sizes['xx-large'],
    });
  }

  private getSettings() :void {
    this.systemSettingApiService.getInvoiceSettings().subscribe((settings :any) => {
      if (settings) {
        this.settings = settings;
        this.cd.detectChanges();
      }
    });
  }

  private hasMultipleWarehouseGroupIDs() :boolean {
    const uniqueWarehouseGroupIDs = new Set(
      this.selectedRows.map((row) => row.warehouseGroup?.id),
    );

    return uniqueWarehouseGroupIDs.size > 1;
  }

  private getUnpostedOrderIds() :number[] {
    return this.selectedRows
      .filter((order) => !order.isPosted)
      .map((order) => order.id);
  }

  private getPreviewPicklistAction() :void {
    this.previewPicklistAction$
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.previewPicklistUtilsService.setAction('')),
      )
      .subscribe((action :string) => {
        if (action) {
          switch (action) {
            case EQuickFulfillmentActions.CREATE_PICKLIST:
              this.onBulkCreatePicklist();
              break;
            case EQuickFulfillmentActions.EXPORT_PACKING_SLIP:
              this.onBulkExportPackingSlips();
              break;
            case EQuickFulfillmentActions.EXPORT_SALES_ORDER:
              this.onBulkExportPackingSlips();
              break;
            case EQuickFulfillmentActions.BULK_UPDATE_TAGS:
              this.onBulkUpdateTags();
              break;
            case EQuickFulfillmentActions.BULK_UPDATE_ORDER_STATUS:
              this.onBulkUpdateOrderStatus();
              break;
            default:
              break;
          }
        }
      });
  }

  private initGroupState() :void {
    const groupState = localStorage.getItem(
      LocalStorageKeys.SALES_ORDER_AG_GROUP_STATE,
    );
    if (groupState) {
      this.currentGroupBy = groupState;
    }
  }

  get redirectToPickAndPack() :boolean {
    return this.ftPickAndPackEnabled;
  }

  get canCreatePicklist() :boolean {
    if (!this.selectedRows.length) {
      return false;
    }

    if (this.hasMultipleWarehouseGroupIDs()) {
      this.toolTipMessage =
        'Selected orders must have the same warehouse group ID before creating a picklist. You cannot select orders with different warehouse group IDs.';

      return false;
    }

    const unpostedOrderIds = this.getUnpostedOrderIds();

    if (unpostedOrderIds.length) {
      this.toolTipMessage =
        'Order ID(s): ' + unpostedOrderIds.join(', ') + ' not posted.';

      return false;
    }

    return true;
  }

  get canShipOrders() :boolean {
    const canShip =
      (this.selectedRows.length > 0 || this.isSelectAllRows) &&
      (this.channelsHasPushOrderMapping ||
        (this.settings.invoice_push_to_oms &&
          this.settings.invoice_push_to_oms_account_id &&
          this.settings.invoice_push_to_oms_store_id));

    return canShip;
  }

  get orderStatus() :Partial<TSalesOrderStatus> {
    const status :Partial<TSalesOrderStatus> = {};

    Object.keys(CSalesOrderStatusConstant).map((key) => {
      switch (key) {
        case ESalesOrderStatusValue.SHIPPED.toUpperCase():
          if (this.canMarkAsShipped) {
            status[key as keyof TSalesOrderStatus] =
              CSalesOrderStatusConstant[key as keyof TSalesOrderStatus];
          }
          break;
        default:
          status[key as keyof TSalesOrderStatus] =
            CSalesOrderStatusConstant[key as keyof TSalesOrderStatus];
          break;
      }
    });

    return status;
  }

  get hasNoPostedOrder() :boolean {
    const unpostedOrderIds = this.getUnpostedOrderIds();

    if (unpostedOrderIds.length) {
      this.toolTipMessage =
        'Order ID(s): ' +
        unpostedOrderIds.join(', ') +
        ' not posted and excluded from the list.';

      return false;
    }

    return true;
  }

  get isInvalidWarehouseGroupFromSelectAllAction() :boolean {
    let warehouseGroupIdsArray :number[] = [];
    if (this.currentQueryParams?.warehouseGroupIds) {
      warehouseGroupIdsArray = this.currentQueryParams.warehouseGroupIds
        .split(',')
        .map(Number);
    }

    return this.isSelectAllRows && warehouseGroupIdsArray?.length > 1;
  }

  get ftPartialFulfillment() :boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      'red_DV-2840_partial_fulfillment',
    );
  }

  onBatchCreateInvoices() :void {
    if (this.selectedRows.length === 0) {
      this.snackBar.open('No orders selected for invoice creation.', 'Close', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    const salesOrdersWithInvoicing :ISalesOrderWithInvoicing[] =
      this.selectedRows.map((order) => ({ ...order }));

    this.dialog
      .open(BatchCreateInvoiceComponent, {
        ...DialogConfig.LARGE,
        data: {
          salesOrders: salesOrdersWithInvoicing,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((hasUpdates :boolean) => {
        if (hasUpdates) {
          this.refreshTable.emit();
        }
      });
  }
}
